import { ref, onMounted } from 'vue';
import { useRequest, getDefaultHeaders, CustomHeaders } from '@local-composable/request';
import { encryptToBackendFormat } from '../utils/crypto';
import { DownloadUrl } from '@local-utils/const';
import { parseUA } from '@local-utils/common';

export function useAttribution({ channel, isToStore = true, isCopy = true, clickParam = {} } = {}) {
  const isIOS = parseUA().ios;
  const attribution_id = ref('');

  // 打开链接
  const attributionOpen = (data) => {
    return useRequest({
      url: '/v2/attribution/open',
      data,
    }).post();
  };

  // 点击事件
  // https://yapi.seeronnet.com/project/1185/interface/api/116690
  const attributionClick = (data) => {
    return useRequest({
      url: '/v2/attribution/click',
      data,
    }).post();
  };

  /**
   * 无缓存时必须同步拿 attribution_id：浏览器剪贴板依赖点击手势，
   * await 异步 click 后手势已失效，第一次复制会失败，第二次才有缓存才能复制。
   */
  const attributionClickSync = (payload) => {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', '/v2/attribution/click', false);
      const headers = { ...getDefaultHeaders(), ...CustomHeaders };
      Object.keys(headers).forEach((key) => {
        if (headers[key]) xhr.setRequestHeader(key, headers[key]);
      });
      const formData = new FormData();
      Object.keys(payload).forEach((key) => {
        const value = payload[key];
        if (value == null) return;
        formData.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
      });
      xhr.send(formData);
      if (xhr.status < 200 || xhr.status >= 300) return '';
      const json = JSON.parse(xhr.responseText || '{}');
      if (![1, 200].includes(json.code)) return '';
      return json.data?.attribution_id || '';
    } catch {
      return '';
    }
  };

  const copyTextNow = (text) => {
    try {
      navigator.clipboard?.writeText?.(text)?.catch(() => {});
    } catch {
      /* ignore */
    }
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', 'readonly');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      textarea.setSelectionRange(0, text.length);
      document.execCommand('copy');
      document.body.removeChild(textarea);
    } catch {
      /* ignore */
    }
  };

  const getEncryptedDeviceInfo = () => {
    const info = {
      screen_width: String(window.screen.width),
      screen_height: String(window.screen.height),
      pixel_ratio: String(window.devicePixelRatio),
    };
    const encrypted = encryptToBackendFormat(info);
    return encrypted;
  };

  /**
   * 须在 onMounted 外先赋值：在工具函数里临时 `useAttribution()` 再 `toDownload()` 时，
   * 没有挂载到组件，onMounted 不会执行，attributionClick 的 encrypted_data 会一直是空串。
   */
  const encrypted_data = ref('');
  if (typeof window !== 'undefined') {
    try {
      encrypted_data.value = getEncryptedDeviceInfo();
    } catch {
      encrypted_data.value = '';
    }
  }

  // 要触发open接口，不能直接在外部调用方法，还是要在页面调用useAttribution

  const tryAttributionOpenOncePerDay = async () => {
    if (typeof window === 'undefined') return;
    const today = new Date().toLocaleDateString();
    const cacheKey = `attribution_open_date_${channel}`;
    const lastDate = localStorage.getItem(cacheKey);
    if (lastDate === today) return;
    const { error } = await attributionOpen({
      channel: channel,
      encrypted_data: encrypted_data.value,
    });
    if (!error?.value) {
      localStorage.setItem(cacheKey, today);
    }
  };

  let openPromise = null;
  onMounted(() => {
    encrypted_data.value = getEncryptedDeviceInfo();
    openPromise = tryAttributionOpenOncePerDay().finally(() => {
      openPromise = null;
    });
    return openPromise;
  });

  const toDownload = async () => {
    if (openPromise) await openPromise;
    encrypted_data.value = getEncryptedDeviceInfo();

    const cacheKey = `attribution_click_data_${channel}`;
    const cachedAttribution = localStorage.getItem(cacheKey);

    if (cachedAttribution) {
      try {
        const { id, expiry } = JSON.parse(cachedAttribution);
        if (Date.now() < expiry) {
          attribution_id.value = id;
        }
      } catch (e) {
        console.error(e);
      }
    }

    if (!attribution_id.value) {
      const clickPayload = {
        channel: channel,
        encrypted_data: encrypted_data.value,
        hidden_behavioral: 1,
        ...clickParam,
      };
      // 无缓存时用同步请求，保证复制仍在点击手势内；失败再回退异步
      attribution_id.value = attributionClickSync(clickPayload);
      if (!attribution_id.value) {
        const { data } = await attributionClick(clickPayload);
        attribution_id.value = data.value?.attribution_id || '';
      }

      if (attribution_id.value) {
        localStorage.setItem(
          cacheKey,
          JSON.stringify({
            id: attribution_id.value,
            expiry: Date.now() + 10 * 60 * 1000,
          }),
        );
      }
    }
    const androidUrl = `https://play.google.com/store/apps/details?id=com.mmc.seer.onnet&referrer=attribution_id%3D${attribution_id.value}`;
    if (attribution_id.value && isCopy) {
      copyTextNow(`seer_at#${attribution_id.value}`);
    }
    if (!isToStore)
      return {
        ios: DownloadUrl.ios,
        android: attribution_id.value ? androidUrl : DownloadUrl.android,
      };

    if (isIOS) {
      window.open(DownloadUrl.ios, '_blank');
    } else if (!attribution_id.value) {
      window.open(DownloadUrl.android, '_blank');
    } else {
      window.open(androidUrl, '_blank');
    }
  };

  return {
    toDownload,
    attribution_id,
  };
}
